import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import Logo from '../../Logo';
import { formatPhoneNumber } from '../../utils';

type LoginStep = 'sms' | 'calendar';

export const LoginNew = ({ apiUrl }: { apiUrl: string }): JSX.Element => {
  const { agentId } = useParams();
  const [searchParams] = useSearchParams();
  const status = searchParams.get('status');
  const missingScopes = searchParams.get('missing_scopes');
  const error = searchParams.get('error');
  const stateParam = searchParams.get('state');
  const [googleLink, setGoogleLink] = useState<string>('');
  const [azureLink, setAzureLink] = useState<string>('');
  const [agentCalendarInfo, setAgentCalendarInfo] = useState<{
    hasGoogle: boolean;
    hasAzure: boolean;
  } | null>(null);
  const [step, setStep] = useState<LoginStep>(() =>
    searchParams.get('step') === 'calendar' ? 'calendar' : 'sms',
  );
  const [isConsentChecked, setIsConsentChecked] = useState<boolean>(false);
  const [userName, setUserName] = useState<string>('');
  const [userPhone, setUserPhone] = useState<string>('');
  const [initialUserName, setInitialUserName] = useState<string>('');
  const [initialUserPhone, setInitialUserPhone] = useState<string>('');
  const [signupLoading, setSignupLoading] = useState<boolean>(false);
  const [signupError, setSignupError] = useState<string | null>(null);
  const [agentNotFoundError, setAgentNotFoundError] = useState<string | null>(
    null,
  );

  useEffect(() => {
    const fetchGoogleLink = async () => {
      try {
        let url = `${apiUrl}/api/auth/google/${agentId}`;
        if (stateParam) {
          url += `?state=${encodeURIComponent(stateParam)}`;
        }
        const response = await fetch(url);
        const jsonData = await response.json();
        setGoogleLink(jsonData.url);
      } catch (err) {
        console.error('An error occurred while fetching data:', err);
      }
    };
    const fetchAzureLink = async () => {
      try {
        let url = `${apiUrl}/api/auth/azure/${agentId}`;
        if (stateParam) {
          url += `?state=${encodeURIComponent(stateParam)}`;
        }
        const response = await fetch(url);
        const jsonData = await response.json();
        setAzureLink(jsonData.url);
      } catch (err) {
        console.error('An error occurred while fetching data:', err);
      }
    };
    const fetchAgentInfo = async () => {
      try {
        const url = `${apiUrl}/api/agent/marketing/${agentId}`;
        const response = await fetch(url);

        if (!response.ok) {
          setAgentCalendarInfo({ hasGoogle: false, hasAzure: false });
          const message =
            response.status === 404
              ? 'Agent not found.'
              : 'Unable to load agent. Please try again.';
          setAgentNotFoundError(message);
          return;
        }

        const jsonData = await response.json();
        setAgentNotFoundError(null);
        // Split the name into first and last name
        let firstName = '';
        let lastName = '';

        if (jsonData.first_name) {
          firstName = jsonData.first_name;
        }
        if (jsonData.last_name) {
          lastName = jsonData.last_name;
        }

        const smsEnrolled = Boolean(
          jsonData.sms_enrolled ?? jsonData.smsEnrolled,
        );
        // Skip to calendar step if account already enrolled (e.g. return visit)
        if (smsEnrolled) {
          setStep('calendar');
        }
        // Prefill editable fields with fetched data
        const fullName = `${firstName} ${lastName}`.trim();
        const formattedPhone = jsonData.phone
          ? formatPhoneNumber(jsonData.phone)
          : '';
        setUserName(fullName);
        setUserPhone(formattedPhone);
        setInitialUserName(fullName);
        setInitialUserPhone(formattedPhone);
        setAgentCalendarInfo({
          hasGoogle: Boolean(jsonData.has_google),
          hasAzure: Boolean(jsonData.has_azure),
        });
      } catch {
        setAgentCalendarInfo({ hasGoogle: false, hasAzure: false });
        setAgentNotFoundError('Unable to load agent. Please try again.');
      }
    };

    fetchGoogleLink()
      .then()
      .catch(() => {});
    fetchAzureLink()
      .then()
      .catch(() => {});
    fetchAgentInfo()
      .then()
      .catch(() => {});
  }, [agentId, apiUrl, stateParam]);

  const handleJoinHouseWhisper = async (): Promise<void> => {
    setSignupError(null);
    setSignupLoading(true);
    try {
      const body: Record<string, string> = {
        agent_id: agentId ?? '',
        muted: isConsentChecked ? 'false' : 'true',
      };
      if (userName.trim() !== initialUserName.trim()) {
        body.name = userName.trim();
      }
      if (userPhone.trim() !== initialUserPhone.trim()) {
        body.phone = userPhone.trim();
      }
      const response = await fetch(`${apiUrl}/api/agent/signup`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(body),
      });
      const text = await response.text();
      if (!response.ok) {
        let message: string;
        try {
          const json = JSON.parse(text) as { error?: string };
          message =
            typeof json?.error === 'string' && json.error.trim()
              ? json.error.trim()
              : 'Something went wrong. Please try again.';
        } catch {
          message = 'Something went wrong. Please try again.';
        }
        setSignupError(message);
        return;
      }
      setStep('calendar');
    } catch {
      setSignupError('Something went wrong. Please try again.');
    } finally {
      setSignupLoading(false);
    }
  };

  const formatScopeName = (scope: string): string => {
    // Remove common prefixes and format for display
    const formatted = scope
      .replace(/^https:\/\/www\.googleapis\.com\/auth\//, '')
      .replace(/^https:\/\/graph\.microsoft\.com\//, '')
      .replace(/\./g, ' ')
      .replace(/\b\w/g, l => l.toUpperCase());

    // Handle specific scope names
    const scopeMap: Record<string, string> = {
      'userinfo.profile': 'Profile Information',
      'userinfo.email': 'Email Address',
      'calendar.events': 'Calendar Events',
      'calendar.readonly': 'Calendar Read Access',
      'gmail.send': 'Send Emails',
      'gmail.compose': 'Compose Emails',
      'gmail.modify': 'Modify Emails',
      'gmail.readonly': 'Read Emails',
      'User.ReadBasic.All': 'Basic Profile Information',
      'Calendars.Read': 'Read Calendar',
      'Calendars.ReadWrite': 'Read and Write Calendar',
      'Mail.Read': 'Read Emails',
      'Mail.ReadWrite': 'Read and Write Emails',
      'Mail.Send': 'Send Emails',
      'Mail.ReadBasic': 'Basic Email Access',
      'Mail.ReadWrite.All': 'Full Email Access',
      'Mail.Send.Shared': 'Send Shared Emails',
      'Mail.Read.Shared': 'Read Shared Emails',
      'Mail.ReadWrite.Shared': 'Read and Write Shared Emails',
      'IMAP.AccessAsUser.All': 'IMAP Email Access',
      'POP.AccessAsUser.All': 'POP Email Access',
      'SMTP.Send': 'SMTP Send Access',
      offline_access: 'Offline Access',
      openid: 'OpenID Connect',
      profile: 'Profile Information',
      email: 'Email Address',
    };

    return scopeMap[formatted.toLowerCase()] || formatted;
  };

  if (status === 'scope_error') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '20px',
          maxWidth: '600px',
          margin: '0 auto',
          textAlign: 'center',
        }}
      >
        <Logo />
        <div style={{ marginTop: '20px' }}>
          <h1 style={{ color: '#d32f2f' }}>Additional Permissions Required</h1>
          <p
            style={{
              fontSize: '16px',
              lineHeight: '1.5',
              marginBottom: '20px',
            }}
          >
            To provide you with the full functionality, we need access to all
            requested permissions. Please try again and make sure to grant all
            the permissions when prompted.
          </p>
          {missingScopes && (
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>
                Missing permissions:
              </p>
              <ul style={{ textAlign: 'left', display: 'inline-block' }}>
                {missingScopes.split(',').map((scope, index) => (
                  <li key={index} style={{ marginBottom: '5px' }}>
                    {formatScopeName(scope.trim())}
                  </li>
                ))}
              </ul>
            </div>
          )}
          {error && (
            <div style={{ marginBottom: '20px' }}>
              <p style={{ fontWeight: 'bold', marginBottom: '10px' }}>Error:</p>
              <p style={{ color: '#d32f2f' }}>{error}</p>
            </div>
          )}
          <div style={{ marginTop: '20px' }}>
            <p style={{ marginBottom: '15px', fontWeight: 'bold' }}>
              Please try signing up again with your preferred provider:
            </p>
            {agentCalendarInfo === null ? (
              <p style={{ color: '#555', fontSize: '14px' }}>Loading…</p>
            ) : (
              <>
                {!agentCalendarInfo.hasAzure && (
                  <div style={{ marginBottom: '15px' }}>
                    <img
                      alt="Sign up with Google"
                      onClick={() => (window.location.href = googleLink)}
                      src="/google-sign-in.svg"
                      style={{
                        cursor: 'pointer',
                        width: '100%',
                        maxWidth: '250px',
                      }}
                    />
                  </div>
                )}
                {!agentCalendarInfo.hasGoogle && (
                  <div>
                    <img
                      alt="Sign up with Microsoft"
                      onClick={() => (window.location.href = azureLink)}
                      src="/ms-sign-in.svg"
                      style={{
                        cursor: 'pointer',
                        width: '100%',
                        maxWidth: '250px',
                      }}
                    />
                  </div>
                )}
              </>
            )}
          </div>
        </div>
      </div>
    );
  }

  if (status === 'success') {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          paddingTop: '20px',
        }}
      >
        <Logo />
        <div style={{ marginTop: '30px', textAlign: 'center' }}>
          <h2>You&apos;re All Set!</h2>
          <p>
            We&apos;re loading your data now. Feel free to close this window —
            we&apos;ll let you know when it&apos;s ready.
          </p>
        </div>
      </div>
    );
  }

  if (agentNotFoundError) {
    return (
      <div
        style={{
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          height: '100vh',
          paddingTop: '100px',
        }}
      >
        <div style={{ position: 'absolute', top: '20px' }}>
          <Logo />
        </div>
        <p
          style={{
            marginTop: '24px',
            textAlign: 'center',
            color: '#d32f2f',
            fontSize: '16px',
            padding: '0 40px',
          }}
        >
          {agentNotFoundError}
        </p>
      </div>
    );
  }

  const linkStyle = {
    color: '#0066cc',
    margin: '0 5px',
    textDecoration: 'underline' as const,
  };

  return (
    <div
      style={{
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        height: '100vh',
        paddingTop: '100px',
      }}
    >
      <div style={{ position: 'absolute', top: '20px' }}>
        <Logo />
      </div>
      <div style={{ width: '100%' }}>
        {step === 'sms' ? (
          <>
            <div
              style={{
                fontSize: '24px',
                lineHeight: '32px',
                textAlign: 'center',
                fontWeight: '300',
              }}
            >
              Welcome to HouseWhisper!
            </div>
            <p
              style={{
                marginTop: '12px',
                textAlign: 'center',
                fontSize: '16px',
                color: '#555',
                lineHeight: '1.5',
              }}
            >
              Your account is ready.
            </p>
            <div
              style={{
                marginTop: '24px',
                width: '100%',
                textAlign: 'center',
              }}
            >
              <div
                style={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'stretch',
                  gap: '16px',
                  maxWidth: '440px',
                  margin: '0 auto',
                  width: '100%',
                }}
              >
                <div
                  style={{
                    display: 'flex',
                    gap: '8px',
                    alignItems: 'center',
                  }}
                >
                  <input
                    aria-label="Name"
                    onChange={e => setUserName(e.target.value)}
                    placeholder="Name"
                    style={{
                      padding: '8px 10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      flex: 1,
                      minWidth: 0,
                      fontSize: '14px',
                    }}
                    type="text"
                    value={userName}
                  />
                  <input
                    aria-label="Phone number"
                    inputMode="tel"
                    onChange={e => setUserPhone(e.target.value)}
                    placeholder="Phone number"
                    style={{
                      padding: '8px 10px',
                      border: '1px solid #ccc',
                      borderRadius: '4px',
                      flex: 1,
                      minWidth: 0,
                      fontSize: '14px',
                    }}
                    type="tel"
                    value={userPhone}
                  />
                </div>
                <div
                  style={{
                    marginTop: '8px',
                    paddingTop: '16px',
                    borderTop: '1px solid #eee',
                  }}
                >
                  <p
                    style={{
                      fontSize: '18px',
                      margin: 0,
                      marginBottom: '12px',
                      color: '#333',
                      lineHeight: '1.5',
                    }}
                  >
                    Would you like to enable SMS lead alerts?
                  </p>
                  <div
                    style={{
                      display: 'flex',
                      alignItems: 'flex-start',
                      gap: '10px',
                      width: '100%',
                    }}
                  >
                    <input
                      checked={isConsentChecked}
                      id="smsConsent"
                      onChange={e => setIsConsentChecked(e.target.checked)}
                      style={{ marginTop: '3px', flexShrink: 0 }}
                      type="checkbox"
                    />
                    <label
                      htmlFor="smsConsent"
                      style={{
                        fontSize: '14px',
                        lineHeight: '1.5',
                        cursor: 'pointer',
                        flex: 1,
                        minWidth: 0,
                        color: '#555',
                      }}
                    >
                      I authorize HouseWhisper to send informational SMS
                      notifications to the phone number above. Message frequency
                      varies. Msg & data rates may apply. Reply STOP to
                      unsubscribe. Reply HELP for help.
                    </label>
                  </div>
                </div>
                {signupError && (
                  <p
                    style={{
                      fontSize: '14px',
                      color: '#d32f2f',
                      margin: 0,
                      marginTop: '8px',
                    }}
                  >
                    {signupError}
                  </p>
                )}
                <button
                  disabled={signupLoading}
                  onClick={() => void handleJoinHouseWhisper()}
                  style={{
                    marginTop: '8px',
                    padding: '12px 24px',
                    fontSize: '16px',
                    fontWeight: 600,
                    color: '#fff',
                    backgroundColor: signupLoading ? '#ccc' : '#5b1eaf',
                    border: 'none',
                    borderRadius: '4px',
                    cursor: signupLoading ? 'not-allowed' : 'pointer',
                    width: '100%',
                  }}
                  type="button"
                >
                  {signupLoading
                    ? 'Signing up…'
                    : isConsentChecked
                      ? 'Enable SMS Alerts'
                      : 'Continue Without SMS'}
                </button>
                <div
                  style={{
                    marginTop: '24px',
                    textAlign: 'center',
                    fontSize: '14px',
                  }}
                >
                  By continuing, you agree to our
                  <a
                    href="https://www.housewhisper.ai/terms-of-service"
                    rel="noopener noreferrer"
                    style={linkStyle}
                    target="_blank"
                  >
                    Terms of Service
                  </a>
                  and
                  <a
                    href="https://www.housewhisper.ai/privacy-policy"
                    rel="noopener noreferrer"
                    style={linkStyle}
                    target="_blank"
                  >
                    Privacy Policy
                  </a>
                  .
                </div>
              </div>
            </div>
          </>
        ) : (
          <>
            <div
              style={{
                fontSize: '24px',
                lineHeight: '32px',
                textAlign: 'center',
                fontWeight: '300',
              }}
            >
              Connect your calendar to finish setting up HouseWhisper.
            </div>
            <p
              style={{
                marginTop: '12px',
                textAlign: 'center',
                fontSize: '16px',
                color: '#555',
              }}
            >
              Sign up with your calendar provider:
            </p>
            {agentCalendarInfo === null ? (
              <p
                style={{
                  marginTop: '24px',
                  textAlign: 'center',
                  color: '#555',
                  fontSize: '14px',
                }}
              >
                Loading…
              </p>
            ) : (
              <>
                {!agentCalendarInfo.hasAzure && (
                  <div
                    style={{
                      marginTop: '24px',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    <img
                      alt="Sign up with Google"
                      onClick={() => (window.location.href = googleLink)}
                      src="/google-sign-in.svg"
                      style={{
                        cursor: 'pointer',
                        width: '100%',
                        maxWidth: '250px',
                      }}
                    />
                  </div>
                )}
                {!agentCalendarInfo.hasGoogle && (
                  <div
                    style={{
                      marginTop: agentCalendarInfo.hasGoogle ? '24px' : '16px',
                      width: '100%',
                      textAlign: 'center',
                    }}
                  >
                    <img
                      alt="Sign up with Microsoft"
                      onClick={() => (window.location.href = azureLink)}
                      src="/ms-sign-in.svg"
                      style={{
                        cursor: 'pointer',
                        width: '100%',
                        maxWidth: '250px',
                      }}
                    />
                  </div>
                )}
              </>
            )}
            <div
              style={{
                marginTop: '24px',
                width: '100%',
                display: 'flex',
                flexDirection: 'column',
                alignItems: 'center',
                justifyContent: 'center',
                fontSize: '14px',
              }}
            >
              <div style={{ textAlign: 'center' }}>
                By signing up, you agree to our
                <a
                  href="https://www.housewhisper.ai/terms-of-service"
                  rel="noopener noreferrer"
                  style={linkStyle}
                  target="_blank"
                >
                  Terms of Service
                </a>
                and
                <a
                  href="https://www.housewhisper.ai/privacy-policy"
                  rel="noopener noreferrer"
                  style={linkStyle}
                  target="_blank"
                >
                  Privacy Policy
                </a>
                .
              </div>
            </div>
          </>
        )}
      </div>
    </div>
  );
};
